/**
 * StaffEmailInput.jsx — username field + staff-domain dropdown.
 *
 * Everywhere the app asks for a staff email the domain is one of
 * STAFF_EMAIL_DOMAINS, so typing it out is pure typo surface. This renders a
 * username box with an @domain select (therapieclinic.com first — the biggest
 * tenant) and reports the combined address through onChange as a plain string,
 * so callers keep their existing `email` state and submit paths untouched.
 *
 * Pasting a full address still works: "bob@therapieclinic.us" splits into
 * username "bob" and selects .us in the dropdown; an unrecognised pasted
 * domain is dropped (the dropdown always shows exactly what will be sent).
 *
 * `directory` turns the box into a live Microsoft 365 type-ahead (SuperAdmin
 * callers only — /api/access/directory): type part of a name or email and pick
 * the person, so a new starter can be added the moment IT creates their account.
 * A typed name with spaces sends nothing until someone is picked; a plain
 * username still works as before. `annotate(email)` adds a note to a suggestion
 * (e.g. the role they already hold).
 */

import React, { useEffect, useRef, useState } from 'react';
import { STAFF_EMAIL_DOMAINS } from '@shared/notificationSchema.js';
import { searchDirectory } from '../api/client.js';

const DEFAULT_DOMAIN = STAFF_EMAIL_DOMAINS[0];
// Local part: letters/digits plus common address punctuation — no spaces or '@'.
const USERNAME_RE = /^[a-z0-9._%+'-]*$/i;
const SEARCH_DEBOUNCE_MS = 250;

function matchDomain(raw) {
  const d = String(raw || '').trim().toLowerCase();
  return STAFF_EMAIL_DOMAINS.find(x => x === d) || null;
}

export default function StaffEmailInput({
  value,
  onChange,
  autoFocus = false,
  placeholder,
  ariaLabel = 'User email',
  inputClass = 'input',
  selectClass = 'select',
  inputStyle,
  selectStyle,
  style,
  onEnter,
  directory = false,
  annotate,
}) {
  // Domain lives here only while the username is empty (value === '' can't
  // carry it); once the value contains a recognised domain, that wins.
  const [pickedDomain, setPickedDomain] = useState(DEFAULT_DOMAIN);
  const [username = '', valueDomain] = String(value || '').split('@');
  const domain = matchDomain(valueDomain) || pickedDomain;

  // Directory mode: the box shows what was typed (a name may have spaces), not
  // just the username part of the value.
  const [text, setText]       = useState(username);
  const [open, setOpen]       = useState(false);
  const [results, setResults] = useState([]);
  const [status, setStatus]   = useState('idle');   // idle | loading | done | error
  const [active, setActive]   = useState(-1);
  const [picked, setPicked]   = useState(null);
  const wrapRef = useRef(null);
  const seqRef  = useRef(0);

  const shown = directory ? text : username;
  const query = text.trim();
  const isNameQuery = /\s/.test(query);
  const usernameOk = directory && isNameQuery ? true : USERNAME_RE.test(directory ? query.split('@')[0] : username);

  // The caller cleared the value (e.g. after a successful add) — clear the box too.
  useEffect(() => {
    if (directory && !value && !/\s/.test(text)) { setText(''); setPicked(null); }
  }, [value]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!directory) return undefined;
    function onDown(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [directory]);

  // Debounced live lookup; a stale reply (slower than a later keystroke) is dropped.
  useEffect(() => {
    if (!directory) return undefined;
    const q = query.split('@')[0];
    if (q.length < 2 || (picked && value === picked.email)) {
      setResults([]); setStatus('idle'); return undefined;
    }
    const seq = ++seqRef.current;
    setStatus('loading');
    const t = setTimeout(async () => {
      try {
        const res = await searchDirectory(q);
        if (seq !== seqRef.current) return;
        setResults(res?.users || []); setStatus('done'); setActive(-1);
      } catch {
        if (seq !== seqRef.current) return;
        setResults([]); setStatus('error');
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [directory, query]);   // eslint-disable-line react-hooks/exhaustive-deps

  function emit(u, d) {
    onChange(u ? `${u}@${d}` : '');
  }

  function handleUsername(raw) {
    let u = raw.replace(/\s+/g, '');
    let d = domain;
    const at = u.indexOf('@');
    if (at >= 0) {
      // A full address was typed or pasted — split it and adopt a known domain.
      const typed = matchDomain(u.slice(at + 1));
      u = u.slice(0, at);
      if (typed) { d = typed; setPickedDomain(typed); }
    }
    emit(u, d);
    return u;
  }

  function handleText(raw) {
    setPicked(null);
    setOpen(true);
    if (/\S\s+\S/.test(raw)) { setText(raw); onChange(''); return; }   // a name — wait for a pick
    const u = handleUsername(raw);
    // Keep a trailing space so "Sarah " can become "Sarah K"; the value never has one.
    setText(raw.includes('@') ? u : raw.trimStart());
  }

  function pick(person) {
    const [u, d] = person.email.split('@');
    const known = matchDomain(d);
    if (known) setPickedDomain(known);
    setText(u);
    setPicked(person);
    setOpen(false);
    onChange(person.email);
  }

  function onKeyDown(e) {
    const listOpen = directory && open && results.length > 0;
    if (listOpen && e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(results.length - 1, i + 1)); return; }
    if (listOpen && e.key === 'ArrowUp')   { e.preventDefault(); setActive(i => Math.max(0, i - 1)); return; }
    if (listOpen && e.key === 'Enter' && active >= 0) { e.preventDefault(); pick(results[active]); return; }
    if (directory && e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'Enter' && onEnter) onEnter(e);
  }

  const showList = directory && open && query.split('@')[0].length >= 2 && !(picked && value === picked.email);
  const pickedNote = directory && picked && value === picked.email
    ? [picked.name, picked.jobTitle].filter(Boolean).join(' · ')
    : null;

  return (
    <div style={style} ref={wrapRef}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', position: 'relative' }}>
        <input
          type="text"
          className={inputClass}
          value={shown}
          autoFocus={autoFocus}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          placeholder={placeholder || (directory ? 'name or username' : 'username')}
          aria-label={ariaLabel}
          role={directory ? 'combobox' : undefined}
          aria-expanded={directory ? showList : undefined}
          aria-autocomplete={directory ? 'list' : undefined}
          onChange={e => (directory ? handleText(e.target.value) : handleUsername(e.target.value))}
          onFocus={() => directory && setOpen(true)}
          onKeyDown={onKeyDown}
          style={{ flex: 1, minWidth: 0, ...inputStyle }}
        />
        <select
          className={selectClass}
          value={domain}
          aria-label="Email domain"
          onChange={e => { setPickedDomain(e.target.value); setPicked(null); emit(isNameQuery ? '' : username, e.target.value); }}
          style={{ flexShrink: 0, width: 'auto', ...selectStyle }}
        >
          {STAFF_EMAIL_DOMAINS.map(d => <option key={d} value={d}>@{d}</option>)}
        </select>
        {showList && (
          <div role="listbox" aria-label="Microsoft 365 directory" className="plain-pop dd-shell" style={{ zIndex: 50 }}>
            {status === 'loading' && !results.length && (
              <div style={{ fontSize: 11.5, color: 'var(--text-3)', padding: '6px var(--dd-item-px)' }}>Searching the directory…</div>
            )}
            {status === 'error' && (
              <div style={{ fontSize: 11.5, color: 'var(--text-3)', padding: '6px var(--dd-item-px)' }}>
                Directory search is unavailable — type the username instead.
              </div>
            )}
            {status === 'done' && !results.length && (
              <div style={{ fontSize: 11.5, color: 'var(--text-3)', padding: '6px var(--dd-item-px)' }}>
                No one in the directory matches “{query}”.
              </div>
            )}
            {results.map((p, i) => {
              const note = annotate ? annotate(p.email) : null;
              return (
                <button
                  key={p.id || p.email}
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  className={`dd-item dd-two${i === active ? ' active' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(p)}
                  style={{ whiteSpace: 'normal' }}
                >
                  <b>{p.name || p.email}</b>
                  <span>{[p.email, p.jobTitle, p.office].filter(Boolean).join(' · ')}</span>
                  {note && <span style={{ color: 'var(--brand-ink, var(--text-2))' }}>{note}</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {pickedNote && (
        <div className="field-hint" style={{ marginTop: 4, color: 'var(--text-2)' }}>✓ {pickedNote}</div>
      )}
      {!usernameOk && (
        <div className="field-hint" style={{ color: 'var(--danger)', marginTop: 4 }}>
          Username can only contain letters, digits and . _ % + ' -
        </div>
      )}
    </div>
  );
}
